import type { MessageSchema } from '@/locales/schema'
import { createI18n } from 'vue-i18n'
import { isProd } from '@/libs/isprod'

// Import all compiled locale files
import enUS from './locale.en-US.json'
import ptBR from './locale.pt-BR.json'
import af from './locale.af.json'
import amET from './locale.am-ET.json'
import ar from './locale.ar.json'
import az from './locale.az.json'
import bg from './locale.bg.json'
import bnIN from './locale.bn-IN.json'
import cs from './locale.cs.json'
import de from './locale.de.json'
import enGB from './locale.en-GB.json'
import es419 from './locale.es-419.json'
import esES from './locale.es-ES.json'
import fa from './locale.fa.json'
import fi from './locale.fi.json'
import fr from './locale.fr.json'
import frCA from './locale.fr-CA.json'
import gu from './locale.gu.json'
import hi from './locale.hi.json'
import hr from './locale.hr.json'
import hu from './locale.hu.json'
import id from './locale.id.json'
import it from './locale.it.json'
import ja from './locale.ja.json'
import ka from './locale.ka.json'
import kn from './locale.kn.json'
import ko from './locale.ko.json'
import ml from './locale.ml.json'
import mr from './locale.mr.json'
import ms from './locale.ms.json'
import omET from './locale.om-ET.json'
import pl from './locale.pl.json'
import pt from './locale.pt.json'
import ro from './locale.ro.json'
import ru from './locale.ru.json'
import si from './locale.si.json'
import sk from './locale.sk.json'
import soET from './locale.so-ET.json'
import srCyrl from './locale.sr-Cyrl.json'
import sv from './locale.sv.json'
import sw from './locale.sw.json'
import ta from './locale.ta.json'
import te from './locale.te.json'
import th from './locale.th.json'
import tr from './locale.tr.json'
import uk from './locale.uk.json'
import ur from './locale.ur.json'
import vi from './locale.vi.json'
import zhCN from './locale.zh-CN.json'

export type Locales =
  | 'af'
  | 'am-ET'
  | 'ar'
  | 'az'
  | 'bg'
  | 'bn-IN'
  | 'cs'
  | 'de'
  | 'en-GB'
  | 'en-US'
  | 'en-NG'
  | 'en-GH'
  | 'en-ZA'
  | 'en-KE'
  | 'es-419'
  | 'es-ES'
  | 'fa'
  | 'fi'
  | 'fr'
  | 'fr-CA'
  | 'gu'
  | 'hi'
  | 'hr'
  | 'hu'
  | 'id'
  | 'it'
  | 'ja'
  | 'ka'
  | 'kn'
  | 'ko'
  | 'ml'
  | 'mr'
  | 'ms'
  | 'om-ET'
  | 'pl'
  | 'pt'
  | 'pt-BR'
  | 'ro'
  | 'ru'
  | 'si'
  | 'sk'
  | 'so-ET'
  | 'sr-Cyrl'
  | 'sv'
  | 'sw'
  | 'ta'
  | 'te'
  | 'th'
  | 'tr'
  | 'uk'
  | 'ur'
  | 'vi'
  | 'zh-CN'

const options = {
  legacy: false,
  locale: 'en-US' satisfies Locales,
  fallbackLocale: 'en-US' satisfies Locales,
  messages: {
    af: af,
    'am-ET': amET,
    ar: ar,
    az: az,
    bg: bg,
    'bn-IN': bnIN,
    cs: cs,
    de: de,
    'en-GB': enGB,
    'en-US': enUS,
    'es-419': es419,
    'es-ES': esES,
    fa: fa,
    fi: fi,
    fr: fr,
    'fr-CA': frCA,
    gu: gu,
    hi: hi,
    hr: hr,
    hu: hu,
    id: id,
    it: it,
    ja: ja,
    ka: ka,
    kn: kn,
    ko: ko,
    ml: ml,
    mr: mr,
    ms: ms,
    'om-ET': omET,
    pl: pl,
    pt: pt,
    'pt-BR': ptBR,
    ro: ro,
    ru: ru,
    si: si,
    sk: sk,
    'so-ET': soET,
    'sr-Cyrl': srCyrl,
    sv: sv,
    sw: sw,
    ta: ta,
    te: te,
    th: th,
    tr: tr,
    uk: uk,
    ur: ur,
    vi: vi,
    'zh-CN': zhCN,
    // Map African English locales to enUS
    'en-NG': enUS,
    'en-GH': enUS,
    'en-ZA': enUS,
    'en-KE': enUS
  },
  missingWarn: !isProd(),
  missingFallback: !isProd()
}

export function initI18n() {
  // @ts-ignore TS2589: Large locale object (48+ locales) causes excessive type depth
  return createI18n<
    {
      message: MessageSchema
    },
    Locales,
    false
  >(options)
}

export type I18nGlobal = ReturnType<typeof initI18n>['global']
